import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { settledList } from '../../utils/loadState'
import { getCustomer, getMappings, getMappingStats, suggestMappings } from '../../api'
import './ValueMappings.css'

export default function ValueMappings() {
  const { customerId } = useParams()
  const [customer, setCustomer] = useState(null)
  const [mappings, setMappings] = useState([])
  const [stats, setStats] = useState(null)
  const [expanded, setExpanded] = useState(null)
  const [loading, setLoading] = useState(true)
  // A failed load is an error with a Retry, never "No value mappings yet" (item 201).
  const [mappingsError, setMappingsError] = useState(null)

  async function load() {
    setLoading(true)
    try {
      const [ctx, maps, st] = await Promise.allSettled([
        getCustomer(customerId), getMappings(customerId), getMappingStats(customerId),
      ])
      if (ctx.status === 'fulfilled') setCustomer(ctx.value)
      const ml = settledList(maps, 'mappings')
      setMappings(ml.items || [])
      setMappingsError(ml.error)
      setStats(st.status === 'fulfilled' ? st.value : null)
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [customerId])

  const companyName = customer?.company_name || customerId
  const breadcrumbs = [
    { label: 'Customers', href: '/customers' },
    { label: companyName, href: `/customers/${customerId}` },
    { label: 'Value Mappings' },
  ]

  if (loading) return <><TopNav breadcrumbs={breadcrumbs} /><div className="vm-loading"><div className="vm-spinner" /><p>Loading…</p></div></>

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} />
      <div className="vm-page">
        <div className="vm-header">
          <h1 className="vm-title">Value Mappings</h1>
          <p className="vm-subtitle">Semantic translations between vendor codes and standard values</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="vm-stats">
            <div className="vm-stat"><div className="vm-stat-value">{stats.total_mappings || 0}</div><div className="vm-stat-label">Mappings</div></div>
            <div className="vm-stat"><div className="vm-stat-value">{stats.total_entries || 0}</div><div className="vm-stat-label">Entries</div></div>
            {stats.by_type && Object.entries(stats.by_type).map(([type, count]) => (
              <div key={type} className="vm-stat"><div className="vm-stat-value">{count}</div><div className="vm-stat-label">{type}</div></div>
            ))}
          </div>
        )}

        {/* Mapping List */}
        {mappingsError ? (
          <LoadError what="value mappings" error={mappingsError} onRetry={load} />
        ) : mappings.length > 0 ? (
          <div className="vm-list">
            {mappings.map((m, i) => (
              <div key={m.id || i} className="vm-card">
                <div className="vm-card-header" onClick={() => setExpanded(expanded === i ? null : i)}>
                  <div className="vm-card-left">
                    <span className="vm-card-type">{m.mapping_type || m.type || 'code'}</span>
                    <div className="vm-card-name">{m.name || 'Unnamed Mapping'}</div>
                    <div className="vm-card-meta">
                      {m.entries?.length || 0} entries
                      {m.source_vendor && ` · ${m.source_vendor}`}
                    </div>
                  </div>
                  <span className="vm-card-toggle">{expanded === i ? '▾' : '▸'}</span>
                </div>
                {expanded === i && m.entries && (
                  <div className="vm-card-entries">
                    <div className="vm-entry-header">
                      <span>Source Value</span><span>→</span><span>Mapped Value</span>
                    </div>
                    {m.entries.map((e, j) => (
                      <div key={j} className="vm-entry-row">
                        <span className="vm-entry-source">{e.source_value || e.from || '?'}</span>
                        <span className="vm-entry-arrow">→</span>
                        <span className="vm-entry-target">{e.target_value || e.to || '?'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="vm-empty">
            <div className="vm-empty-icon">🔀</div>
            <h3>No value mappings yet</h3>
            <p>Mappings are created when Sempirical encounters vendor-specific codes that need translation.</p>
          </div>
        )}
      </div>
    </>
  )
}
